import React, { useEffect, useState, useRef } from 'react'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { IconButton } from '@material-ui/core'
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  getCountries,
} from 'libphonenumber-js'
import { isEmpty, isNil, prop } from 'ramda'
import { makeStyles } from '@material-ui/core/styles'
import ClearIcon from '@material-ui/icons/Clear'
import clsx from 'clsx'
import LabelError from 'app/componentV4/Labels/LabelError'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp'
import { twMerge } from 'tailwind-merge'
import { FormattedMessage, useIntl } from 'react-intl'
import Assets from 'app/configs/assetConfigs'

const useStyles = makeStyles((theme) => ({
  popup: {
    width: '30rem !important',
  },
  root: {
    position: 'relative',
    '& .react-tel-input .special-label': {
      display: 'none !important',
    },
    '& .react-tel-input .form-control': {
      backgroundColor: '#FFFFFF',
      borderRadius: '5px',
      height: '48px',
      color: theme.palette.primary.main,
      width: '100%',
      boxShadow: 'none',
      paddingRight: '40px',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&:focus': {
        boxShadow: 'none',
      },
    },
  },
  valid: {
    '& .react-tel-input .form-control': {
      border: `1px solid ${theme.palette.grey[300]}`,
      '&:hover': {
        borderColor: `${theme.palette.grey[300]}!important`,
      },
      '&:focus': {
        borderColor: `${theme.palette.grey[300]}!important`,
      },
    },
  },
  error: {
    '& .react-tel-input .form-control': {
      border: `1px solid ${theme.palette.error.main}`,
      '&:hover': {
        borderColor: `${theme.palette.error.main}!important`,
      },
      '&:focus': {
        borderColor: `${theme.palette.error.main}!important`,
      },
    },
  },
  clearIcon: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.grey[500],
    '&:hover': {
      color: theme.palette.grey[700],
    },
  },
  countryDropdown: {
    width: '100%',
  },
  flagIcon: {
    fontSize: '24px',
    marginRight: '8px',
  },
  popupCountry: {
    maxHeight: '400px',
    width: '330px',
  },
}))

export const validatePhoneNumber = (phoneNumber) => {
  if (isEmpty(phoneNumber) || isNil(phoneNumber)) return true
  const parsedNumber = parsePhoneNumberFromString(phoneNumber)
  if (!parsedNumber) {
    return false
  }
  return parsedNumber.isValid()
}

export const isOnlyCountryCallingCode = (input) => {
  const validCountryCodes = getCountries().map(
    (code) => `+${getCountryCallingCode(code)}`,
  )
  return validCountryCodes.includes(input)
}

const CountryFlagSelect = ({ value = 'US', onChange, options }) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState(options[0])

  const dropdownRef = useRef(null)

  const filteredCountries = options.filter((country) => {
    const digital = `+${getCountryCallingCode(country.value)}`
    return (
      country.label.toLowerCase().includes(search.toLowerCase()) ||
      digital.toLowerCase().includes(search.toLowerCase())
    )
  })

  const handleSelect = (country) => {
    setSelectedCountry(country)
    setIsOpen(false)

    if (onChange) onChange(country.value)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setSelectedCountry(
      options.find(
        (country) => country.value.toLowerCase() === value.toLowerCase(),
      ) || options[0],
    )
  }, [value])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
    }
  }, [isOpen])

  return (
    <div className="relative w-64 p-2" ref={dropdownRef}>
      <button
        className="flex items-center justify-between w-full p-2"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <div className="flex items-center space-x-2">
          <img
            src={`https://flagcdn.com/w40/${selectedCountry.value.toLowerCase()}.png`}
            alt={selectedCountry.label}
            className="w-full"
            onError={(e) => (e.target.src = Assets.imageDefault)}
          />
        </div>
        {isOpen ? (
          <ArrowDropUpIcon fontSize="small" className="text-gray-500" />
        ) : (
          <ArrowDropDownIcon fontSize="small" className="text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div
          className={twMerge(
            'mt-5 p-2 flex flex-col absolute bg-white shadow z-10 rounded-8',
            classes.popupCountry,
          )}
        >
          <div className="p-2 w-full">
            <input
              type="text"
              className="w-full p-5 border rounded-8 border-gray-300 text-14"
              placeholder={formatMessage({ id: 'search' })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-320 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <div
                  key={country.value}
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer space-x-2 text-16"
                  onClick={() => handleSelect(country)}
                >
                  <span className="ml-2">{country.label}</span>
                  <span>(+{getCountryCallingCode(country.value)})</span>
                </div>
              ))
            ) : (
              <p className="p-2 text-gray-500 text-14">
                <FormattedMessage id="crm.form.receiveMethods.option.empty" />
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const PhoneNumberInputV4 = ({
  value,
  error,
  helperText,
  id,
  onChange,
  prefer = [],
}) => {
  const classes = useStyles()

  const [countryCode, setCountryCode] = useState('US')

  const handleClear = (e) => {
    e.stopPropagation()
    setCountryCode('US')
    onChange('')
  }

  useEffect(() => {
    if (!value) {
      setCountryCode('US')
      return
    }

    const phoneNumberObj = parsePhoneNumberFromString(value)

    if (!phoneNumberObj && !countryCode) {
      setCountryCode('US')
    } else {
      if (prop('country', phoneNumberObj)) {
        setCountryCode(phoneNumberObj.country)
      }
    }
  }, [value])

  return (
    <div>
      <div
        className={clsx(error ? classes.error : classes.valid, classes.root)}
      >
        <PhoneInput
          key={id}
          international
          defaultCountry={countryCode}
          country={countryCode}
          value={value}
          countryCallingCodeEditable={false}
          onChange={(number = '') => onChange(number)}
          countries={prefer && prefer.length > 0 ? prefer : undefined}
          countrySelectComponent={CountryFlagSelect}
          className={clsx(
            'border h-48 rounded-8',
            error ? 'border-red' : 'border-gray-300',
          )}
        />
        {value && (
          <IconButton
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            onClick={handleClear}
          >
            <ClearIcon />
          </IconButton>
        )}
      </div>
      {error && <LabelError id={id} text={helperText} />}
    </div>
  )
}

export default PhoneNumberInputV4
